// File: frontend/src/components/common/DomainTabBar.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import "./DomainTabBar.css";
import { FaHashtag, FaCameraRetro, FaPaperPlane, FaCalendarAlt, FaShieldAlt, FaRunning, FaUsers } from "react-icons/fa";

type PaneMode = "matches" | "people" | "clubs" | "teams" | "topics" | "istikram" | "birak";

interface DomainTabBarProps {
  paneMode: PaneMode;
  // App.tsx owns paneMode; on mobile the handler also flips the visible pane
  // back to the list so tapping a domain always lands on that domain's list.
  onDomainChange: (mode: PaneMode) => void;
  // Right-aligned slot for future bar-level actions (extra buttons, shortcuts).
  // Currently unused — the filters render in the header next to the search bar.
  children?: React.ReactNode;
}

// The domain tabs, split into two GROUPS instead of one flat list.
//
// Group 1 — Gündem, İstikram, Bırak: these three are really the same
// underlying "custom topic" domain. İstikram and Bırak are not separate
// content types; they are custom topics whose FIRST channel ("kanal") is the
// reserved "İstikram" / "Bırak" system channel (see CustomTopicService /
// getSystemChannelTopics on the backend). A topic routed into one of these
// two never appears in Gündem's own list. Grouping them tight together (no
// gap) reads that relationship visually, before the wider gap to group 2.
//
// Group 2 — Maç, Kulüp, Camia, Takım: the four "real" sport content domains,
// unrelated to each other and to group 1.
//
// Kept as two arrays instead of one flat list so the gap between them can be
// styled without hardcoding index math, and so a future domain only needs to
// be added to the right group's array.
const DOMAIN_TABS_GROUP_1: Array<{
  mode: PaneMode;
  i18nKey: string;
  fallback: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  // When true this tab ALWAYS renders icon-only, ignoring SHOW_TAB_ICONS
  // below — the name still reads on hover via the button's title=
  // attribute (already set unconditionally). İstikram and Bırak are
  // icon-only by design (a camera / a paper plane stand in for the words),
  // not part of the global icons on/off switch, so each needs its own
  // per-tab override.
  iconOnly?: boolean;
}> = [
  { mode: "topics",   i18nKey: "left_pane_mode_topics",   fallback: "Gündem",   Icon: FaHashtag },
  { mode: "istikram", i18nKey: "left_pane_mode_istikram", fallback: "İstikram", Icon: FaCameraRetro, iconOnly: true },
  { mode: "birak",    i18nKey: "left_pane_mode_birak",    fallback: "Bırak",    Icon: FaPaperPlane, iconOnly: true },
];

const DOMAIN_TABS_GROUP_2: Array<{
  mode: PaneMode;
  i18nKey: string;
  fallback: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  iconOnly?: boolean;
}> = [
  { mode: "matches", i18nKey: "left_pane_mode_matches", fallback: "Maç",    Icon: FaCalendarAlt },
  { mode: "clubs",   i18nKey: "left_pane_mode_clubs",   fallback: "Kulüp",  Icon: FaShieldAlt },
  { mode: "people",  i18nKey: "left_pane_mode_people",  fallback: "Camia",  Icon: FaRunning },
  { mode: "teams",   i18nKey: "left_pane_mode_teams",   fallback: "Takım",  Icon: FaUsers },
];

// One-line switch: set to false to render the domain tabs TEXT-ONLY
// (sözlük-style, no icons anywhere) — EXCEPT İstikram and Bırak, which are
// icon-only regardless of this switch (see iconOnly on DOMAIN_TABS_GROUP_1).
// Flip this if the icons still bother the eye on the other five tabs.
const SHOW_TAB_ICONS = false;

// Icon/label alignment — INLINE on purpose, so it cannot be lost to a stale
// or cached stylesheet. Two rules make the row line up:
// 1) Icons get a FIXED HEIGHT and auto width. Font Awesome glyphs have
//    different aspect ratios (FaUsers is wide, FaRunning is narrow); forcing
//    them into a square box letterboxes each one differently, which is what
//    made the Maç calendar sit visibly higher than its neighbors and its own
//    label. With height-only sizing every icon shares the exact same top and
//    bottom edge.
// 2) The icon height (12px) is close to the text's cap height, and the
//    2px translateY drops its optical center onto the letters' center.
//    These two numbers are THE tuning knobs — nothing else moves the icon.
const ICON_STYLE: React.CSSProperties = {
  display: "block",
  height: 12,
  width: "auto",
  transform: "translateY(2px)",
};

const LABEL_STYLE: React.CSSProperties = {
  display: "block",
  height: 13,
  lineHeight: "13px",
};

// İstikram's camera and Bırak's paper plane are bigger than the other tabs'
// inline icons and aren't vertically nudged to sit next to text (there is no
// text — each icon is centered by the button's own flex alignment instead).
// A CSS class can't override ICON_STYLE above since it's applied inline on
// the same element (inline styles always win over a class), so this needs
// its own object.
//
// 18px, not a rounder number: DomainTabBar.css sizes
// .domain-tab-bar__tab--icon-only's padding so padding + this height totals
// EXACTLY a text tab's natural height (30px) — that's what keeps the whole
// bar the same height as the plain-text version. Changing this number
// requires changing that CSS padding to match; see the comment there.
//
// width is a FIXED number, not "auto" like ICON_STYLE — each Font Awesome
// glyph has a different intrinsic aspect ratio (camera is wider than tall,
// a paper plane is diagonal), so auto-width gives every icon a DIFFERENT
// bounding box. justify-content:center on the button centers that box, but
// a lopsided box still reads as off-center to the eye. A fixed square box
// removes that per-icon variance entirely — the SVG's own viewBox scaling
// (preserveAspectRatio, on by default) letterboxes and centers the glyph
// within it for us.
const ICON_ONLY_STYLE: React.CSSProperties = {
  display: "block",
  height: 18,
  width: 18,
};

/**
 * Full-width horizontal domain bar rendered above the three content columns.
 * Replaces the old mode toggle that lived inside the left pane header — the
 * five domains now read as top-level site sections (sözlük convention) and
 * the bar can grow horizontally as new domains/buttons are added. On narrow
 * screens the bar scrolls horizontally instead of wrapping.
 */
export const DomainTabBar: React.FC<DomainTabBarProps> = ({ paneMode, onDomainChange, children }) => {
  const { t } = useTranslation();

  // Prevents the button from receiving focus on click so the browser won't
  // scroll the container to bring the focused button into view.
  const noFocus = (e: React.MouseEvent) => e.preventDefault();

  return (
    <nav className="domain-tab-bar" aria-label={t("domain_bar_aria", "Bölümler")}>
      <div className="domain-tab-bar__scroll">
        {/* Group 1: Gündem / İstikram / Bırak — same custom-topic domain, kept
            visually tight together (see the comment on DOMAIN_TABS_GROUP_1). */}
        <div className="domain-tab-bar__group">
          {DOMAIN_TABS_GROUP_1.map(({ mode, i18nKey, fallback, Icon, iconOnly }) => (
            <button
              key={mode}
              onMouseDown={noFocus}
              className={`domain-tab-bar__tab ${iconOnly ? "domain-tab-bar__tab--icon-only" : ""} ${paneMode === mode ? "active" : ""}`}
              onClick={() => onDomainChange(mode)}
              title={t(i18nKey, fallback)}
              aria-label={t(i18nKey, fallback)}
            >
              {(iconOnly || SHOW_TAB_ICONS) && (
                <Icon className="domain-tab-bar__icon" style={iconOnly ? ICON_ONLY_STYLE : ICON_STYLE} />
              )}
              {/* İstikram (iconOnly) shows the camera and nothing else — the
                  name is still available on hover via title= above, and to
                  screen readers via aria-label on the button. */}
              {!iconOnly && (
                <span className="domain-tab-bar__label" style={LABEL_STYLE}>{t(i18nKey, fallback)}</span>
              )}
            </button>
          ))}
        </div>

        {/* A visible vertical rule between the two groups — margin alone read
            as "one bar with slightly odd spacing" rather than two distinct
            clusters, so there's now an actual divider element, not just a
            gap. Decorative only (aria-hidden); collapses to the mobile
            horizontal border-top instead (see DomainTabBar.css). */}
        <div className="domain-tab-bar__divider" aria-hidden="true" />

        {/* Group 2: Maç / Kulüp / Camia / Takım — the four sport content
            domains, visually separated from group 1 by the divider above.
            On mobile the two groups wrap onto their own rows instead
            (see DomainTabBar.css). */}
        <div className="domain-tab-bar__group domain-tab-bar__group--2">
          {DOMAIN_TABS_GROUP_2.map(({ mode, i18nKey, fallback, Icon }) => (
            <button
              key={mode}
              onMouseDown={noFocus}
              className={`domain-tab-bar__tab ${paneMode === mode ? "active" : ""}`}
              onClick={() => onDomainChange(mode)}
              title={t(i18nKey, fallback)}
            >
              {SHOW_TAB_ICONS && <Icon className="domain-tab-bar__icon" style={ICON_STYLE} />}
              <span className="domain-tab-bar__label" style={LABEL_STYLE}>{t(i18nKey, fallback)}</span>
            </button>
          ))}
        </div>
        {/* Future bar items (new domains, shortcuts) append into group 2 and
            the row keeps spreading horizontally — no layout change needed. */}
      </div>

      {/* Right-aligned actions slot for future bar-level buttons. Rendered
          OUTSIDE the scroll container so slot content never scrolls away with
          the tabs and is not clipped by overflow-x. */}
      {children && (
        <div className="domain-tab-bar__actions">
          {children}
        </div>
      )}
    </nav>
  );
};